import React, { lazy, Suspense } from 'react';
import { lazyCoeur } from './utils/lazy79';
import { VersionBandeau } from './components/auth/VersionBandeau';
import { FactureProvider } from './components/finance/FactureModal';
import { ToastCheck } from './components/ToastCheck';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { AuthCallback } from './components/AuthCallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { AuthPage } from './pages/AuthPage';
import MissionsPage from './components/missions/MissionsSabrilPage';
import { CompteCredits, CreditsBandeau, RechargerHost } from './components/credits/Credits40';
import { DashboardLayout } from './components/DashboardLayout';

import { Plus360Boundary } from './components/plus/Plus360Boundary';

// Racine : le site vitrine, sauf sur un sous-domaine « app. » (accès direct à la connexion)
const RootEntry = () => {
  const host = window.location.hostname;
  if (host.startsWith('app.')) return <Navigate to="/admin" replace />;
  // Application installée (écran d'accueil du téléphone) : on rouvre directement le restaurant, pas la vitrine.
  const standalone = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true || new URLSearchParams(window.location.search).get('source') === 'pwa';
  let last = null; try { last = localStorage.getItem('r360_last_app'); } catch { /* stockage indisponible */ }
  if (standalone && last) return <Navigate to={last} replace />;
  // Preview : on travaille le site client → la racine affiche le site (comme en production).
  return <VitrineV3 />;
};
import { EmployeeFirstLoginTour } from './components/EmployeeFirstLoginTour';
import { SubscriptionGateProvider } from './contexts/SubscriptionGateContext';
import { I18nProvider } from './utils/i18n';
import { AIChatBubble } from './components/AIChatBubble';
import { NetworkActivity } from './components/NetworkActivity';
import { GuideReader } from './components/book/GuideReader';
import { OnboardingTour } from './components/OnboardingTour';
import { Toaster } from './components/ui/sonner';
import { IgConfirmProvider } from './components/ig/IgSheet';
import { PourquoiGrise } from './components/ig/ChoixMois';
import { UpsellMount } from './components/UpsellMount';
import { ModuleAccessBoundary } from './components/modules/ModuleAccessBoundary';
import { useAuth } from './contexts/AuthContext';
import { LiensInternes } from './components/LiensInternes';

// Admin Pages
import { AdminProtectedRoute } from './components/AdminProtectedRoute';

// LOT 71 · P10 · chaque page se charge à la demande
const TenantPersonalizationPage = lazy(() => import('./pages/TenantPersonalizationPage'));
const AccesPage = lazy(() => import('./pages/AccesPage'));
const Demarrage48Page = lazy(() => import('./pages/Demarrage48Page'));
const PremierJourPage = lazy(() => import('./pages/PremierJourPage'));
const AdminOnboarding48Page = lazy(() => import('./pages/admin/AdminOnboarding48Page').then((m) => ({ default: m.AdminOnboarding48Page })));
const AdminImportTenantPage = lazy(() => import('./pages/admin/AdminImportTenantPage').then((m) => ({ default: m.AdminImportTenantPage })));
const RoleOnboardingPage = lazy(() => import('./pages/RoleOnboardingPage').then((m) => ({ default: m.RoleOnboardingPage })));
const AccueilIgPage = lazyCoeur(() => import('./pages/AccueilIgPage'));
const StocksIgPage = lazy(() => import('./pages/StocksIgPage'));
const DLCPage = lazy(() => import('./pages/DLCPage').then((m) => ({ default: m.DLCPage })));
const EquipeIgPage = lazyCoeur(() => import('./pages/EquipeIgPage'));
const MoiPage = lazy(() => import('./pages/MoiPage'));
const TablettePage = lazy(() => import('./pages/TablettePage'));
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const Base360Page = lazy(() => import('./pages/Base360Page'));
const ComptePage = lazy(() => import('./pages/ComptePage'));
const PlusPage = lazy(() => import('./pages/PlusPage').then((m) => ({ default: m.PlusPage })));
const FichesTechniquesPage = lazyCoeur(() => import('./pages/FichesTechniquesPage').then((m) => ({ default: m.FichesTechniquesPage })));
const CuisineFichePage = lazy(() => import('./pages/CuisineFichePage').then((m) => ({ default: m.CuisineFichePage })));
const FoodCostPage = lazy(() => import('./pages/FoodCostPage'));
const EcartPage = lazy(() => import('./pages/EcartPage'));
const EcartsListePage = lazy(() => import('./pages/EcartsListePage'));
const FinanceHubPage = lazy(() => import('./pages/FinanceHubPage'));
const RapportsPage = lazy(() => import('./pages/RapportsPage'));
const BornePage = lazy(() => import('./pages/BornePage'));
const ConnexionPage = lazy(() => import('./pages/ConnexionPage'));
const Admin360Page = lazy(() => import('./pages/admin360/Admin360Page'));
const Admin360FichePage = lazy(() => import('./pages/admin360/Admin360FichePage'));
const DesignPage = lazy(() => import('./pages/DesignPage'));
const RevenuePage = lazy(() => import('./pages/RevenuePage'));
const ChecklistsPage = lazy(() => import('./pages/ChecklistsPage').then((m) => ({ default: m.ChecklistsPage })));
const MeteoPage = lazy(() => import('./pages/MeteoPage').then((m) => ({ default: m.MeteoPage })));
const ExpertsPage = lazy(() => import('./pages/ExpertsPage'));
const ImportsPage = lazy(() => import('./pages/ImportsPage'));
const BookWeeklyPage = lazy(() => import('./pages/BookWeeklyPage'));
const DevelopperCAPage = lazy(() => import('./pages/DevelopperCAPage').then((m) => ({ default: m.DevelopperCAPage })));
const ConsultantDashboardPage = lazy(() => import('./pages/ConsultantDashboardPage').then((m) => ({ default: m.ConsultantDashboardPage })));
const ProgressionPage = lazy(() => import('./pages/ProgressionPage').then((m) => ({ default: m.ProgressionPage })));
const RelationClientPage = lazy(() => import('./pages/RelationClientPage'));
const VentesPage = lazy(() => import('./pages/VentesPage').then((m) => ({ default: m.VentesPage })));
const RecrutementIgPage = lazy(() => import('./pages/RecrutementIgPage'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage'));
const AvisPublicPage = lazy(() => import('./pages/AvisPublicPage'));
const AchatsIgPage = lazy(() => import('./pages/AchatsIgPage'));
const CommunicationMetaPage = lazy(() => import('./pages/CommunicationMetaPage'));
const LiaisonsPage = lazy(() => import('./pages/LiaisonsPage').then((m) => ({ default: m.LiaisonsPage })));
const LivraisonsPage = lazy(() => import('./pages/LivraisonsPage').then((m) => ({ default: m.LivraisonsPage })));
const EtiquettesPage = lazy(() => import('./pages/EtiquettesPage').then((m) => ({ default: m.EtiquettesPage })));
const HaccpHubPage = lazy(() => import('./pages/HaccpHubPage').then((m) => ({ default: m.HaccpHubPage })));
const HygieneMetaPage = lazy(() => import('./pages/HygieneMetaPage'));
const HaccpPassportPage = lazy(() => import('./pages/HaccpPassportPage'));
const TemperaturesPage = lazy(() => import('./pages/TemperaturesPage').then((m) => ({ default: m.TemperaturesPage })));
const OnboardingAdminPage = lazy(() => import('./pages/OnboardingAdminPage').then((m) => ({ default: m.OnboardingAdminPage })));
const FormationPage = lazy(() => import('./pages/FormationPage'));
const MyOnboardingPage = lazy(() => import('./pages/MyOnboardingPage'));
const POSConnectorsPage = lazy(() => import('./pages/POSConnectorsPage').then((m) => ({ default: m.POSConnectorsPage })));
const ProGestionPage = lazy(() => import('./pages/ProGestionPage').then((m) => ({ default: m.ProGestionPage })));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })));
const InboxAIPage = lazy(() => import('./pages/InboxAIPage').then((m) => ({ default: m.InboxAIPage })));
const SocialMediaPage = lazy(() => import('./pages/SocialMediaPage'));
const SitePage = lazy(() => import('./pages/site/SitePage'));
const VitrineV3 = lazy(() => import('./pages/site/VitrineV3'));
const AdminPacksPage = lazy(() => import('./pages/admin/AdminPacksPage'));
const AdminListeAttentePage = lazy(() => import('./pages/admin/AdminListeAttentePage'));
const ListeAttentePage = lazy(() => import('./pages/site/ListeAttentePage'));  // LOT 82 · A
const FichesPage = lazy(() => import('./pages/site/FichesPage'));  // LOT 82 · page publique /fiches
const VersListeAttente = () => { const { search } = useLocation(); return <Navigate to={`/liste-attente${search}`} replace />; };
const AbonnementPage = lazy(() => import('./pages/site/AbonnementPage'));
const BienvenuePage = lazy(() => import('./pages/site/BienvenuePage'));
const MiseEnRoutePage = lazy(() => import('./pages/MiseEnRoutePage'));
const SitePrivacyPage = lazy(() => import('./pages/site/SitePrivacyPage'));
const SiteSecurityPage = lazy(() => import('./pages/site/SiteSecurityPage'));
const SiteMentionsLegalesPage = lazy(() => import('./pages/site/SiteMentionsLegalesPage'));
const SiteCGVPage = lazy(() => import('./pages/site/SiteCGVPage'));
const SiteContactPage = lazy(() => import('./pages/site/SiteContactPage'));
const Site404Page = lazy(() => import('./pages/site/Site404Page'));
const MarginAutopilotPage = lazy(() => import('./pages/MarginAutopilotPage').then((m) => ({ default: m.MarginAutopilotPage })));
const AutomatisationsPage = lazy(() => import('./pages/AutomatisationsPage').then((m) => ({ default: m.AutomatisationsPage })));
const AutomatisationsRedirect = lazy(() => import('./pages/AutomatisationsRedirect').then((m) => ({ default: m.AutomatisationsRedirect })));
const AidePage = lazy(() => import('./pages/AidePage').then((m) => ({ default: m.AidePage })));
const HistoirePage = lazy(() => import('./pages/HistoirePage').then((m) => ({ default: m.HistoirePage })));
const StudioDesignPage = lazy(() => import('./pages/StudioDesignPage'));
const PreviewPage = lazy(() => import('./pages/PreviewPage').then((m) => ({ default: m.PreviewPage })));
const MagicLinkPage = lazy(() => import('./pages/MagicLinkPage'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const SetupWizardPage = lazy(() => import('./pages/SetupWizardPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })));
const CreateClientPage = lazy(() => import('./pages/admin/CreateClientPage').then((m) => ({ default: m.CreateClientPage })));
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage').then((m) => ({ default: m.AdminLoginPage })));
const AdminAuthCallback = lazy(() => import('./pages/admin/AdminAuthCallback').then((m) => ({ default: m.AdminAuthCallback })));
const AdminStatsPage = lazy(() => import('./pages/admin/AdminStatsPage').then((m) => ({ default: m.AdminStatsPage })));
const AdminLot13dPage = lazy(() => import('./pages/admin/AdminLot13dPage'));
const PageAttente = () => <div className="sq71-page" data-testid="page-attente" aria-busy="true"><i /><i /><i /></div>;  // LOT 71 · P10 · jamais d'écran blanc pendant le chargement d'une page

// Tenant Routes Component - wraps all client-specific routes with TenantProvider
function TenantRoutes() {
  const { tenantSlug } = useParams();
  const location = useLocation();

  // V6.0.28 — iOS-style page transitions (slide-in from right + crossfade).
  // Track history idx to invert the direction on POP (back) vs PUSH (forward).
  const prevIdx = React.useRef(window.history.state?.idx ?? 0);
  const [navDir, setNavDir] = React.useState(1); // 1 = forward, -1 = back
  React.useEffect(() => {
    const cur = window.history.state?.idx ?? 0;
    setNavDir(cur < prevIdx.current ? -1 : 1);
    prevIdx.current = cur;
  }, [location.key]);

  // Only animate the inner page area (sub-path) — keep the route key stable per first path segment
  // so that staying on /taches with query params doesn't re-animate.
  const pageKey = location.pathname;
  
  return (
    <TenantProvider key={tenantSlug}>
      <FactureProvider>
      <RechargerHost />
      <div className="m360-top"><CreditsBandeau /></div>
      <Suspense fallback={<PageAttente />}><Routes>
        <Route path="missions" element={<ProtectedRoute><DashboardLayout title="Missions Sabril" showBack hideTitle><MissionsPage /></DashboardLayout></ProtectedRoute>} />
        <Route path="base360" element={<ProtectedRoute><DashboardLayout title="Base 360" showBack hideTitle><Base360Page /></DashboardLayout></ProtectedRoute>} />
        <Route path="compte-credits" element={<ProtectedRoute><DashboardLayout title="Mon compte · Crédits" showBack><CompteCredits /></DashboardLayout></ProtectedRoute>} />
        <Route path="compte" element={<ProtectedRoute><DashboardLayout title="Mon compte" showBack hideTitle><ComptePage /></DashboardLayout></ProtectedRoute>} />
        <Route path="login" element={<AuthPage tenantSlug={tenantSlug} />} />
        <Route path="auth" element={<AuthPage tenantSlug={tenantSlug} />} />
        <Route path="demo" element={<AuthPage tenantSlug={tenantSlug} directDemo />} />
        <Route path="personnalisation" element={<ProtectedRoute><TenantPersonalizationPage /></ProtectedRoute>} />
        <Route path="mon-compte" element={<Navigate to="../dashboard?feuille=moi" replace />} />
        <Route path="acces" element={<ProtectedRoute><AccesPage /></ProtectedRoute>} />
        {/* Brief FINAL (écran 35) · Explorer, Actions, Tableau de bord, Piloter, Cockpit, Abonnement, Crédits, 360+ → une seule page : l'Accueil et ses feuilles */}
        <Route path="actions" element={<ToAccueil feuilleFocus />} />
        <Route path="tableau-de-bord" element={<ToAccueil onglet="plans" />} />
        <Route path="reglages" element={<ToAccueil feuille="reglages" />} />
        <Route path="demarrage" element={<ProtectedRoute><Demarrage48Page /></ProtectedRoute>} />
        <Route path="lieux" element={<Navigate to="../equipe?vue=lieux" replace />} />
        <Route path="auth/callback" element={<AuthCallback />} />
        <Route path="role-setup" element={<ProtectedRoute skipOnboardingCheck={true}><RoleOnboardingPage /></ProtectedRoute>} />
        <Route path="setup-wizard" element={<ProtectedRoute skipOnboardingCheck={true}><SetupWizardPage /></ProtectedRoute>} />
        <Route path="mise-en-route" element={<ProtectedRoute skipOnboardingCheck={true}><MiseEnRoutePage /></ProtectedRoute>} />
        <Route path="premier-jour" element={<ProtectedRoute><PremierJourPage /></ProtectedRoute>} />
        
        {/* Main Navigation */}
        <Route path="dashboard" element={<ProtectedRoute><AccueilIgPage /></ProtectedRoute>} />
        <Route path="explorer" element={<ToAccueil />} />
        <Route path="planning" element={<Navigate to="../equipe?vue=planning" replace />} />
        <Route path="stocks" element={<ProtectedRoute><StocksIgPage /></ProtectedRoute>} />
        <Route path="stocks-liste" element={<Navigate to="../stocks?vue=produits" replace />} />
        <Route path="livraisons" element={<ProtectedRoute><Plus360Boundary moduleKey="livraison_360" title="Livraison 360"><LivraisonsPage /></Plus360Boundary></ProtectedRoute>} />
        <Route path="plus-360" element={<ToAccueil feuille="abonnement" />} />
        <Route path="etiquettes" element={<ProtectedRoute><EtiquettesPage /></ProtectedRoute>} />
        <Route path="haccp" element={<Navigate to="../hygiene" replace />} />
        <Route path="haccp/hub" element={<ProtectedRoute><HaccpHubPage /></ProtectedRoute>} />
        <Route path="haccp/passeport" element={<ProtectedRoute><HaccpPassportPage /></ProtectedRoute>} />
        <Route path="haccp/temperatures" element={<ProtectedRoute><TemperaturesPage /></ProtectedRoute>} />
        <Route path="temperatures" element={<ToHaccpTemp />} />
        <Route path="haccp/rapport" element={<Navigate to="../hygiene?vue=documents&rapport=1" replace />} />
        <Route path="onboarding" element={<ProtectedRoute><OnboardingAdminPage /></ProtectedRoute>} />
        <Route path="mon-parcours" element={<ProtectedRoute><ModuleAccessBoundary moduleKey="basecamp" title="Formation"><FormationPage vueDefaut="parcours" /></ModuleAccessBoundary></ProtectedRoute>} />
        <Route path="connecteurs-caisse" element={<ProtectedRoute><POSConnectorsPage /></ProtectedRoute>} />
        <Route path="pro-gestion" element={<ProtectedRoute><ModuleAccessBoundary moduleKey="pro_gestion" title="Pro Gestion"><ProGestionPage /></ModuleAccessBoundary></ProtectedRoute>} />
        <Route path="integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
        <Route path="inbox" element={<ProtectedRoute><ModuleAccessBoundary moduleKey="secretary_ai" title="Secrétaire"><InboxAIPage /></ModuleAccessBoundary></ProtectedRoute>} />
        <Route path="social" element={<ToSocialMedia mode="reseaux" />} />
        <Route path="margin-autopilot" element={<ProtectedRoute><Plus360Boundary moduleKey="margin_autopilot" title="Margin Auto-Pilot"><MarginAutopilotPage /></Plus360Boundary></ProtectedRoute>} />
        <Route path="automatisations" element={<ProtectedRoute><AutomatisationsRedirect /></ProtectedRoute>} />
        {/* V6.0.32 — strategie/* redirects to base camp (École Stratégique absorbée dans n16-n23) */}
        <Route path="strategie" element={<Navigate to={`/${tenantSlug}/mon-parcours`} replace />} />
        <Route path="strategie/*" element={<Navigate to={`/${tenantSlug}/mon-parcours`} replace />} />
        <Route path="mon-abonnement" element={<ToAccueil feuille="abonnement" />} />
        <Route path="credits" element={<Navigate to="../compte-credits" replace />} />
        <Route path="evaluation" element={<Navigate to="../taches?classement=1" replace />} />
        {/* V6.0.32 — formation supprimée, tout est dans Formation */}
        <Route path="formation" element={<ProtectedRoute><ModuleAccessBoundary moduleKey="basecamp" title="Formation"><FormationPage /></ModuleAccessBoundary></ProtectedRoute>} />
        <Route path="formation/:roleKey" element={<Navigate to={`/${tenantSlug}/mon-parcours`} replace />} />
        <Route path="equipe" element={<ProtectedRoute><EquipeIgPage /></ProtectedRoute>} />
        <Route path="moi" element={<ProtectedRoute><MoiPage /></ProtectedRoute>} />
        <Route path="tablette" element={<ProtectedRoute><TablettePage /></ProtectedRoute>} />
        <Route path="plus" element={<ProtectedRoute><PlusPage /></ProtectedRoute>} />
        
        {/* Secondary Modules */}
        <Route path="pointage" element={<Navigate to="../equipe?vue=presences" replace />} />
        <Route path="dlc" element={<ProtectedRoute><DLCPage /></ProtectedRoute>} />
        {/* 01.1 · R1 URL = nom du menu : « Recettes & coûts » → /recettes-couts (ancienne /fiches redirige) */}
        <Route path="recettes-couts" element={<ProtectedRoute><FichesTechniquesPage /></ProtectedRoute>} />
        <Route path="recettes-couts/preparations" element={<ProtectedRoute><FichesTechniquesPage /></ProtectedRoute>} />
        {/* Fiche technique recette (cahier 6 oct.) · R1 : /recettes-couts/[plat] ou /recettes-couts/[carte]/[plat] si deux plats ont le même nom */}
        <Route path="recettes-couts/:ficheSlug" element={<ProtectedRoute><FichesTechniquesPage /></ProtectedRoute>} />
        <Route path="recettes-couts/:menuSlug/:ficheSlug" element={<ProtectedRoute><FichesTechniquesPage /></ProtectedRoute>} />
        {/* Affichage cuisine (tablette) : fiche en lecture seule, sans coûts · connexion salarié requise */}
        <Route path="cuisine/fiche/:ficheKey" element={<ProtectedRoute><CuisineFichePage /></ProtectedRoute>} />
        <Route path="fiches" element={<ToRecettesCouts />} />
        <Route path="food-cost" element={<ProtectedRoute><FoodCostPage /></ProtectedRoute>} />
        <Route path="materiel" element={<Navigate to="../stocks?mode=equipements" replace />} />
        <Route path="finance" element={<ProtectedRoute><FinanceHubPage /></ProtectedRoute>} />
        <Route path="ecart/:id" element={<ProtectedRoute><EcartPage /></ProtectedRoute>} />
        <Route path="ecarts" element={<ProtectedRoute><EcartsListePage /></ProtectedRoute>} />
        <Route path="rapports" element={<ProtectedRoute><RapportsPage /></ProtectedRoute>} />
        <Route path="borne" element={<ProtectedRoute><BornePage /></ProtectedRoute>} />
        <Route path="formations" element={<Navigate to={`/${tenantSlug}/mon-parcours`} replace />} />
        <Route path="documents" element={<ToEquipe onglet="documents" />} />
        <Route path="conges" element={<ToEquipe onglet="conges" />} />
        <Route path="commandes" element={<Navigate to="../achats?vue=commandes" replace />} />
        <Route path="facturation" element={<Navigate to={`/${tenantSlug}/plus360`} replace />} />
        <Route path="facturation/*" element={<Navigate to={`/${tenantSlug}/plus360`} replace />} />
        <Route path="checklists" element={<ProtectedRoute><ChecklistsPage /></ProtectedRoute>} />
        <Route path="dashboard-ca" element={<Navigate to={`/${tenantSlug}/finance?vue=ecart`} replace />} />
        <Route path="chiffre-affaires" element={<ProtectedRoute><RevenuePage /></ProtectedRoute>} />
        <Route path="dashboard-financier" element={<Navigate to={`/${tenantSlug}/finance?vue=resultat`} replace />} />
        <Route path="meteo" element={<ProtectedRoute><MeteoPage /></ProtectedRoute>} />
        <Route path="cockpit" element={<ToAccueil feuille="reglages" />} />
        <Route path="parametres" element={<ToAccueil feuille="reglages" />} />
        <Route path="chat" element={<ProtectedRoute><ExpertsPage /></ProtectedRoute>} />
        <Route path="imports" element={<ProtectedRoute><ImportsPage /></ProtectedRoute>} />
        <Route path="assistant" element={<Navigate to="../chat" replace />} />
        <Route path="book-semaine" element={<ProtectedRoute><BookWeeklyPage /></ProtectedRoute>} />
        <Route path="developper-ca" element={<ProtectedRoute><DevelopperCAPage /></ProtectedRoute>} />
        <Route path="rentabilite" element={<Navigate to={`/${tenantSlug}/finance?vue=ventes`} replace />} />
        <Route path="performance" element={<Navigate to="../taches?classement=1" replace />} />
        <Route path="commandes-recurrentes" element={<ToCommandesRecurrentes />} />
        <Route path="achats-recurrents" element={<ToCommandesRecurrentes />} />
        <Route path="achats" element={<ProtectedRoute><AchatsIgPage /></ProtectedRoute>} />
        <Route path="achats/liste-articles" element={<Navigate to="../achats?vue=catalogue" replace />} />
        <Route path="prix-achat" element={<Navigate to="../achats?vue=catalogue&f=prix_hausse" replace />} />
        <Route path="produire" element={<Navigate to="../recettes-couts" replace />} />
        <Route path="piloter" element={<ToAccueil />} />
        <Route path="equipe-hub" element={<Navigate to="../equipe" replace />} />
        <Route path="communiquer" element={<ProtectedRoute><CommunicationMetaPage /></ProtectedRoute>} />
        <Route path="liaisons" element={<ProtectedRoute><LiaisonsPage /></ProtectedRoute>} />
        <Route path="postes" element={<Navigate to="../dashboard?feuille=postes" replace />} />
        <Route path="articles-achetes" element={<Navigate to="../achats?vue=catalogue" replace />} />
        <Route path="progression" element={<ProtectedRoute><ProgressionPage /></ProtectedRoute>} />
        <Route path="relation-client" element={<ProtectedRoute><Plus360Boundary moduleKey="relation_client" title="Relation client"><RelationClientPage /></Plus360Boundary></ProtectedRoute>} />
        <Route path="juridique" element={<ToEquipe onglet="contrat" />} />
        <Route path="configuration" element={<ToAccueil feuille="reglages" />} />
        <Route path="consultant" element={<ProtectedRoute><ConsultantDashboardPage /></ProtectedRoute>} />
        <Route path="tendances" element={<ToFiches />} />
        <Route path="caisse" element={<Navigate to="../finance" replace />} />
        <Route path="hygiene" element={<ProtectedRoute><HygieneMetaPage /></ProtectedRoute>} />
        <Route path="visibilite" element={<Navigate to="../social-media" replace />} />
        <Route path="fournisseurs" element={<Navigate to="../achats?vue=fournisseurs" replace />} />
        <Route path="fournisseurs/:slug" element={<Navigate to="../achats?vue=fournisseurs" replace />} />
        <Route path="taches" element={<Navigate to="../equipe?vue=taches" replace />} />
        <Route path="ventes" element={<ProtectedRoute><VentesPage /></ProtectedRoute>} />
        <Route path="reservations" element={<ProtectedRoute><ReservationsPage /></ProtectedRoute>} />
        <Route path="recrutement" element={<ProtectedRoute><Plus360Boundary moduleKey="recrutement" title="Recrutement"><RecrutementIgPage /></Plus360Boundary></ProtectedRoute>} />
        <Route path="tickets" element={<Navigate to={`/${tenantSlug}/commandes?piece=ticket`} replace />} />
        <Route path="aide" element={<ProtectedRoute><AidePage /></ProtectedRoute>} />
        <Route path="histoire" element={<HistoirePage />} />
        <Route path="reseaux-sociaux" element={<ToSocialMedia mode="reseaux" />} />
        <Route path="social-media" element={<ProtectedRoute><Plus360Boundary moduleKey="social_360" title="Réseaux sociaux"><SocialMediaPage /></Plus360Boundary></ProtectedRoute>} />
        <Route path="digital" element={<ToSocialMedia mode="performance" />} />
        <Route path="operations" element={<Navigate to="../taches" replace />} />
        <Route path="da" element={<ToSocialMedia mode="da" />} />
        <Route path="studio-design" element={<ProtectedRoute><Plus360Boundary moduleKey="studio_design" title="Studio Design"><StudioDesignPage /></Plus360Boundary></ProtectedRoute>} />
        
        <Route path="" element={<Navigate to={`/${tenantSlug}/dashboard`} replace />} />
        <Route path="*" element={<Navigate to={`/${tenantSlug}/dashboard`} replace />} />
      </Routes></Suspense>
      </FactureProvider>
      {/* V6.0.71 — Global upsell layer (popup + banner + FAB) */}
      <UpsellMount />
    </TenantProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user: moi } = useAuth();
  // LOT 63 · W4 · une adresse construite avec l'identifiant technique revient au nom court, sans recharger
  const seg0 = location.pathname.split('/')[1];
  if (moi?.tenant_slug && moi.tenant_id && moi.tenant_id !== moi.tenant_slug && seg0 === moi.tenant_id) {
    return <Navigate replace to={`/${moi.tenant_slug}${location.pathname.slice(seg0.length + 1)}${location.search}`} />;
  }
  
  // Handle OAuth callbacks with session_id in hash
  if (location.hash?.includes('session_id=')) {
    // Check if it's an admin callback or client callback
    if (location.pathname.startsWith('/admin')) {
      return <AdminAuthCallback />;
    }
    // Extract tenant slug from path for proper routing
    const pathParts = location.pathname.split('/').filter(Boolean);
    const tenantSlugFromPath = pathParts[0]; // e.g., "bensimon" from "/bensimon/dashboard"
    
    // Wrap AuthCallback in TenantProvider with the extracted slug
    return (
      <Suspense fallback={<PageAttente />}><Routes>
        <Route path="/:tenantSlug/*" element={
          <TenantProvider>
            <AuthCallback />
          </TenantProvider>
        } />
        <Route path="*" element={<AuthCallback />} />
      </Routes></Suspense>
    );
  }

  return (
    <Suspense fallback={<PageAttente />}><Routes>
      {/* Admin Routes - 360 Resto Dashboard */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/callback" element={<AdminAuthCallback />} />
      <Route path="/admin" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
      <Route path="/admin/clients/new" element={<AdminProtectedRoute><CreateClientPage /></AdminProtectedRoute>} />
      <Route path="/admin/clients/:clientId/edit" element={<AdminProtectedRoute><CreateClientPage /></AdminProtectedRoute>} />
      <Route path="/admin/stats" element={<AdminProtectedRoute><AdminStatsPage /></AdminProtectedRoute>} />
      <Route path="/admin/onboarding" element={<AdminProtectedRoute><AdminOnboarding48Page /></AdminProtectedRoute>} />
      <Route path="/admin/packs" element={<AdminProtectedRoute><AdminPacksPage /></AdminProtectedRoute>} />
      <Route path="/admin/liste-attente" element={<AdminProtectedRoute><AdminListeAttentePage /></AdminProtectedRoute>} />
      <Route path="/admin/import" element={<AdminProtectedRoute><AdminImportTenantPage /></AdminProtectedRoute>} />
      <Route path="/admin/avocado-lot13d" element={<AdminProtectedRoute><AdminLot13dPage /></AdminProtectedRoute>} />
      <Route path="/admin/settings" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
      
      {/* Legacy routes (redirect to /avocado) */}
      <Route path="/rdv/:token" element={<BookingPage />} />
      <Route path="/avis/:token" element={<AvisPublicPage />} />
      <Route path="/login" element={<Navigate to="/connexion" replace />} />
      <Route path="/connexion" element={<ConnexionPage />} />
      <Route path="/design" element={<DesignPage />} />
      <Route path="/admin-360" element={<Admin360Page />} />
      <Route path="/admin-360/:slug" element={<Admin360FichePage />} />
      <Route path="/auth" element={<Navigate to="/avocado/auth" replace />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/dashboard" element={<Navigate to="/avocado/dashboard" replace />} />
      <Route path="/role-setup" element={<Navigate to="/avocado/role-setup" replace />} />

      {/* Public Preview route (shareable demo link) */}
      <Route path="/preview/:token" element={<PreviewPage />} />

      {/* V5.99.7 — Magic link landing (after Stripe payment) */}
      <Route path="/auth/magic" element={<MagicLinkPage />} />
      
      {/* Tenant Routes - Dynamic client paths */}
      <Route path="/:tenantSlug/*" element={<TenantRoutes />} />
      
      {/* Root redirects to admin */}
      <Route path="/site" element={<VitrineV3 />} />
      <Route path="/liste-attente" element={<ListeAttentePage />} />
      <Route path="/fiches" element={<FichesPage />} />
      <Route path="/attente" element={<VersListeAttente />} />
      <Route path="/go" element={<VersListeAttente />} />
      <Route path="/site/ancien" element={<SitePage />} />
      <Route path="/site/abonnement" element={<AbonnementPage />} />
      <Route path="/site/bienvenue" element={<BienvenuePage />} />
      <Route path="/site/confidentialite" element={<SitePrivacyPage />} />
      <Route path="/site/securite" element={<SiteSecurityPage />} />
      <Route path="/site/mentions-legales" element={<SiteMentionsLegalesPage />} />
      <Route path="/site/cgv" element={<SiteCGVPage />} />
      <Route path="/site/contact" element={<SiteContactPage />} />
      <Route path="/site/*" element={<Site404Page />} />
      <Route path="/" element={<RootEntry />} />
    </Routes></Suspense>
  );
}

const ToSocialMedia = ({ mode }) => { const { tenantSlug } = useParams(); const { search } = useLocation(); const sp = new URLSearchParams(search); sp.set('mode', mode); return <Navigate to={`/${tenantSlug}/social-media?${sp.toString()}`} replace />; };
// Mise en route + Paramètres → Cockpit 360 (un seul écran) ; ?step=X devient ?bloc=X
const ToHaccpTemp = () => { const { tenantSlug } = useParams(); return <Navigate to={`/${tenantSlug}/haccp/temperatures`} replace />; };
// Point 02.1 · « Commandes récurrentes » n'a plus de page dédiée : on redirige vers Commandes & factures, panneau ouvert (en gardant « à valider » si présent).
const ToCommandesRecurrentes = () => { const { tenantSlug } = useParams(); const { search } = useLocation(); const p = new URLSearchParams(search); const rec = p.get('filtre') === 'a_valider' ? 'a_valider' : '1'; return <Navigate to={`/${tenantSlug}/commandes?recurrentes=${rec}`} replace />; };
const ToEquipe = ({ onglet }) => { const { tenantSlug } = useParams(); return <Navigate to={`/${tenantSlug}/equipe?onglet=${onglet}`} replace />; };
/* Brief FINAL · les anciennes entrées (Explorer, Actions, Tableau de bord, Piloter, Cockpit, Abonnement, Crédits, 360+) renvoient vers l'Accueil et sa feuille */
const ToAccueil = ({ feuille, onglet, feuilleFocus }) => { const { tenantSlug } = useParams(); const { search } = useLocation(); const sp = new URLSearchParams(search); const focus = sp.get('focus'); const n = new URLSearchParams(); if (onglet) n.set('onglet', onglet); if (feuille) n.set('feuille', feuille); if (feuilleFocus && focus) n.set('feuille', `decision:${focus}`); else if (feuilleFocus && sp.get('filtre') === 'urgent') n.set('onglet', 'urgentes'); const q = n.toString(); return <Navigate to={`/${tenantSlug}/dashboard${q ? `?${q}` : ''}`} replace />; };
const ToFiches = () => { const { tenantSlug } = useParams(); return <Navigate to={`/${tenantSlug}/recettes-couts`} replace />; };
const ToRecettesCouts = () => { const { tenantSlug } = useParams(); const { search } = useLocation(); return <Navigate to={`/${tenantSlug}/recettes-couts${search || ''}`} replace />; };

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <SubscriptionGateProvider>
              <IgConfirmProvider>
              <LiensInternes />
              <AppRouter />
              <OnboardingTourWrapper />
              <EmployeeTourWrapper />
              <AIChatBubbleWrapper />
              <PourquoiGrise />
              <VersionBandeau />
              <Toaster position="top-center" icons={{ success: <ToastCheck /> }} toastOptions={{ className: "toast-ios", duration: 3500 }} />
              </IgConfirmProvider>
            </SubscriptionGateProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

// Wrapper for onboarding tour - only show for logged in users
// Les visites guidées n'existent que dans l'app d'un établissement (/{slug}/page) — jamais sur le site public (/, /site, /connexion…), ni sur la connexion du tenant.
const SITE_ROOTS = new Set(['site', 'admin', 'auth', 'preview', 'connexion', 'login', 'dashboard', 'role-setup']);
const TENANT_ENTRY = new Set(['login', 'auth', 'demo', 'auth/callback', 'role-setup', 'setup-wizard', 'mise-en-route']);
export const isTenantAppPath = (pathname) => {
  const seg = (pathname || '').split('/').filter(Boolean);
  if (seg.length < 2 || SITE_ROOTS.has(seg[0])) return false;
  return !TENANT_ENTRY.has(seg.slice(1).join('/'));
};

function OnboardingTourWrapper() {
  const { user } = useAuth();
  const location = useLocation();

  // Don't show on login, auth pages, role-setup page, admin pages,
  // OR on the Formation / Mon Parcours page (which has its own avatar selection flow
  // that the tour would block — V5.99 fix).
  // V5.99.13 — Also skip on /setup-wizard so the first-login setup is reachable.
  // V6.0.98 — Also skip on /mon-abonnement so the subscription/drawer flow isn't blocked
  // by the welcome tour (the tour is a feature-discovery tour, not a settings tour).
  const excludedPaths = ['/login', '/auth', '/role-setup', '/admin'];
  const excludedSegments = ['/mon-parcours', '/role-onboarding', '/onboarding', '/setup-wizard', '/mon-abonnement', '/da', '/social-media', '/haccp'];
  if (!user || user.demo_mode || user.poste_id === 'controleur_gestion') return null;  // LOT 26 · pas de visite guidée générique pour le contrôleur
  if (!isTenantAppPath(location.pathname)) return null;
  if (excludedPaths.some(path => location.pathname.startsWith(path))) return null;
  if (excludedSegments.some(seg => location.pathname.endsWith(seg) || location.pathname.includes(seg + '/'))) return null;

  return <OnboardingTour />;
}

// V5.99.8 — Employee first-login tour (5 role-aware steps + PWA install)
function EmployeeTourWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  const excluded = ['/login', '/auth', '/role-setup', '/setup-wizard', '/admin', '/preview'];
  if (!user || user.poste_id === 'controleur_gestion' || user.role === 'consultant') return null;  // LOT 26 · pas la visite « équipier »
  if (!isTenantAppPath(location.pathname)) return null;
  if (excluded.some((p) => location.pathname.startsWith(p))) return null;
  // Replay support: ?tour=1 in the URL forces the tour even after completion.
  const params = new URLSearchParams(location.search);
  const force = params.get('tour') === '1';
  return <EmployeeFirstLoginTour forceShow={force} />;
}

// Wrapper to show AI chat only when logged in and not on pages with dedicated experts
function AIChatBubbleWrapper() {
  const { user } = useAuth();
  const location = useLocation();
  
  // Le bandeau des experts (AgentStoriesBar) ouvre ce chat sur chaque module : on ne le démonte que hors app.
  const pagesWithExpert = [
    '/role-setup',
    '/login',
    '/auth',
    '/admin'
  ];
  
  const hasExpertOnPage = pagesWithExpert.some(path => location.pathname.includes(path));
  
  if (!user || hasExpertOnPage || !isTenantAppPath(location.pathname)) return null;
  return <><NetworkActivity /><AIChatBubble /><GuideReader /></>;
}

export default App;